/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { CheckCircle, XCircle, X, Download, Lock, Printer, ShoppingBag } from 'lucide-react'
import Address from '../Address'
import SavedAddress from '../SavedAddress'
import LoadingScreen from '../LoadingScreen'
import qrCodeImage from '../../assets/qrcode.png'
import { auth, db, functions } from '../../utils/firebase'
import { doc, getDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { toast } from 'sonner'

export default function Checkout({ cart = [], setCart, onAuthClick, isCartLoading = false }) {
    const navigate = useNavigate();
    const [loggedIn, setLoggedIn] = useState(() => !!auth.currentUser);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [addresses, setAddresses] = useState([]);
    const [schemeId, setSchemeId] = useState(null);

    const handleRemoveFromCart = (indexToRemove) => {
        if (setCart) {
            setCart(prev => prev.filter((_, idx) => idx !== indexToRemove));
        }
    };
    const paymentOption = 'cash-on-delivery';

    // Modal states for Address
    const [isSavedAddressModalOpen, setIsSavedAddressModalOpen] = useState(false);
    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
    const [addressModalMode, setAddressModalMode] = useState('create');
    const [editAddressIndex, setEditAddressIndex] = useState(null);

    // Form fields
    const [orderNotes, setOrderNotes] = useState('');

    // Popup state
    const [showPopup, setShowPopup] = useState(false);
    const [popupStatus, setPopupStatus] = useState(''); // 'success' or 'error'
    const [popupMessage, setPopupMessage] = useState('');
    const [orderDetails, setOrderDetails] = useState(null);
    const [isPlacingOrder, setIsPlacingOrder] = useState(false);

    // Re-check login state and load addresses from Firestore
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            setLoggedIn(!!user);
            if (user) {
                try {
                    let userDocRef = doc(db, "users", user.uid);
                    let docSnap = await getDoc(userDocRef);
                    if (!docSnap.exists()) {
                        userDocRef = doc(db, "users", user.email.toLowerCase());
                        docSnap = await getDoc(userDocRef);
                    }
                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        setSchemeId(data.scheme_id || null);
                        const addrList = data.addresses || [];
                        setAddresses(addrList);

                        const selKey = `selectedAddressIndex_${user.email.toLowerCase()}`;
                        const selectedIndex = parseInt(localStorage.getItem(selKey) || '0', 10);
                        if (addrList.length > 0 && addrList[selectedIndex]) {
                            setSelectedAddress(addrList[selectedIndex]);
                        } else if (addrList.length > 0) {
                            setSelectedAddress(addrList[0]);
                        }
                    }
                } catch (e) {
                    console.error("Error loading user addresses", e);
                }
            } else {
                setAddresses([]);
                setSelectedAddress(null);
            }
        });
        return () => unsubscribe();
    }, [loggedIn]);

    const getProductPriceForQty = (product, qty) => {
        const activeSchemeId = schemeId;
        if (!activeSchemeId || !product.pricing) {
            return product.price || product.distributorPrice || 0;
        }

        const schemePricing = product.pricing.find(p => p.scheme_id === activeSchemeId);
        if (!schemePricing || !schemePricing.tiers || schemePricing.tiers.length === 0) {
            return product.price || product.distributorPrice || 0;
        }

        const ascTiers = [...schemePricing.tiers].sort((a, b) => {
            const qa = Number(a.min || a.min_qty || a.minQty || 0);
            const qb = Number(b.min || b.min_qty || b.minQty || 0);
            return qa - qb;
        });

        const matched = [...ascTiers].reverse().find(t => qty >= Number(t.min || t.min_qty || t.minQty || 0));
        return matched ? Number(matched.price || 0) : Number(ascTiers[0]?.price || product.price || product.distributorPrice || 0);
    };

    const subtotal = cart.reduce((sum, item) => {
        const unitPrice = getProductPriceForQty(item.product, Number(item.quantity) || 0);
        return sum + (unitPrice * (Number(item.quantity) || 0));
    }, 0);
    const tax = cart.reduce((sum, item) => {
        if (!item.product.taxApplicable) return sum;
        const taxPercent = Number(item.product.taxPercent) || 0;
        const unitPrice = getProductPriceForQty(item.product, Number(item.quantity) || 0);
        return sum + (taxPercent / 100) * unitPrice * (Number(item.quantity) || 0);
    }, 0);
    const discount = 0;
    const shipping = 0;
    const processing = 0;
    const total = Math.max(0, subtotal + tax + shipping + processing - discount);

    const hasInsufficientStockItems = cart.some(item =>
        item.product.stock !== undefined &&
        (item.quantity > item.product.stock || item.product.stock < (item.product.minimum_order || 1))
    );

    const handleCheckout = async () => {
        if (cart.length === 0) {
            setPopupStatus('error');
            setPopupMessage('Your cart is empty. Please add items to proceed.');
            setShowPopup(true);
            return;
        }

        if (!selectedAddress) {
            setPopupStatus('error');
            setPopupMessage('Please select a delivery address.');
            setShowPopup(true);
            return;
        }

        setIsPlacingOrder(true);
        try {
            const placeOrderCallable = httpsCallable(functions, 'placeOrder');
            const result = await placeOrderCallable({
                address: selectedAddress,
                paymentMethod: paymentOption === 'cash-on-delivery' ? 'COD' : paymentOption,
                orderNotes,
                platform: "web"
            });

            const responseData = result.data;
            if (responseData && responseData.success) {
                // Fetch the placed order details from Firestore orders collection
                const orderDocId = responseData.data.order_id;
                const orderSnap = await getDoc(doc(db, "orders", orderDocId));

                if (orderSnap.exists()) {
                    const orderData = orderSnap.data();

                    const newOrder = {
                        orderId: orderData.orderId,
                        total: Number(orderData.finance?.finalAmount || 0).toFixed(2),
                        method: orderData.paymentMethod || paymentOption,
                        type: 'delivery',
                        purchaseDate: orderData.createdAt?.toDate
                            ? orderData.createdAt.toDate().toLocaleDateString()
                            : new Date().toLocaleDateString(),
                        itemCount: orderData.items?.length || 0,
                        items: orderData.items || [],
                        finance: orderData.finance || {},
                        address: orderData.address || {}
                    };

                    setCart([]); // Clear cart locally (since backend cleared it in Firestore)
                    navigate('/order-success', { state: { orderDetails: newOrder } });
                } else {
                    throw new Error("Order was placed but details could not be retrieved from database.");
                }
            } else {
                setPopupStatus('error');
                setPopupMessage(responseData?.message || 'Failed to place order.');
                setShowPopup(true);
            }
        } catch (error) {
            console.error("Error calling placeOrder Callable:", error);
            setPopupStatus('error');
            setPopupMessage(error?.message || 'An error occurred while placing the order.');
            setShowPopup(true);
        } finally {
            setIsPlacingOrder(false);
        }
    };

    const getInvoiceHTML = (orderDetails) => {
        const sub = Number(orderDetails.finance?.subTotal || 0);
        const t = Number(orderDetails.finance?.tax || 0);
        const ship = Number(orderDetails.finance?.deliveryCharge || 0);
        const proc = Number(orderDetails.finance?.processingFee || 0);
        const disc = Number(orderDetails.finance?.discount || 0);
        const finalAmt = Number(orderDetails.finance?.finalAmount || 0);

        const cgstVal = (t / 2).toFixed(2);
        const sgstVal = (t / 2).toFixed(2);

        const addressParts = [
            orderDetails.address?.streetAddress,
            orderDetails.address?.unitNumber ? `Unit ${orderDetails.address.unitNumber}` : null,
            [orderDetails.address?.city, orderDetails.address?.state, orderDetails.address?.zipCode].filter(Boolean).join(', ')
        ].filter(Boolean).join('<br>');

        const itemsList = orderDetails.items || [];
        const totalTaxableAmountCalc = itemsList.reduce((acc, item) => acc + (parseFloat(item.price || 0) * parseFloat(item.cartQty || item.quantity || 1)), 0);
        const totalGstAmountCalc = itemsList.reduce((acc, item) => {
            const unitPrice = parseFloat(item.price || 0);
            const qty = parseFloat(item.cartQty || item.quantity || 1);
            const taxable = unitPrice * qty;
            const taxPct = Number(item.taxPercent || item.product?.taxPercent || 0);
            return acc + (taxPct > 0 ? (taxable * taxPct / 100) : 0);
        }, 0);

        const totalTaxableAmount = totalTaxableAmountCalc > 0 ? totalTaxableAmountCalc : sub;
        const totalGstAmount = totalGstAmountCalc > 0 ? totalGstAmountCalc : t;

        const itemsRowsHtml = itemsList.map((product, index) => {
            const unitPrice = parseFloat(product.price || 0);
            const qty = parseFloat(product.cartQty || product.quantity || 1);
            const taxableAmt = unitPrice * qty;
            const code = (product.productCodeLetters || '') + (product.productCode || product.code || '');
            const hsn = product.hsn || product.hsnCode || product.product?.hsn || product.product?.hsnCode || '—';
            const taxPercent = Number(product.taxPercent || product.product?.taxPercent || 0);
            const taxAmt = taxPercent > 0 ? (taxableAmt * taxPercent / 100) : 0;
            const lineTotal = taxableAmt + taxAmt;
            const unit = product.unit || product.unitType || product.unitsOfMeasurement || product.pack || product.unitName || product.product?.unit || product.product?.unitType || product.product?.unitsOfMeasurement || product.product?.pack || '—';

            return `
                <tr style="page-break-inside: avoid; break-inside: avoid;">
                    <td style="padding: 8px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000; vertical-align: top;">${index + 1}</td>
                    <td style="padding: 8px; border-right: 1px solid #000; border-bottom: 1px solid #000; vertical-align: top;">
                        <div style="font-weight: bold; color: #000;">${product.productName || product.title || '—'}</div>
                        <div style="font-size: 9px; color: #555; margin-top: 2px;">
                            Code: ${code || '—'}
                        </div>
                    </td>
                    <td style="padding: 8px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000; vertical-align: top;">${hsn}</td>
                    <td style="padding: 8px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000; vertical-align: top;">${qty}</td>
                    <td style="padding: 8px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000; vertical-align: top;">${unit}</td>
                    <td style="padding: 8px; text-align: right; border-right: 1px solid #000; border-bottom: 1px solid #000; vertical-align: top;">₹${unitPrice.toFixed(2)}</td>
                    <td style="padding: 8px; text-align: right; border-right: 1px solid #000; border-bottom: 1px solid #000; vertical-align: top;">₹${taxableAmt.toFixed(2)}</td>
                    <td style="padding: 8px; text-align: center; border-right: 1px solid #000; border-bottom: 1px solid #000; vertical-align: top;">
                        ${taxPercent > 0 ? `₹${taxAmt.toFixed(2)}<div style="font-size: 9px; color: #555;">(${taxPercent}%)</div>` : '—'}
                    </td>
                    <td style="padding: 8px; text-align: right; border-bottom: 1px solid #000; vertical-align: top; font-weight: bold;">₹${lineTotal.toFixed(2)}</td>
                </tr>
            `;
        }).join('');

        return `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Invoice - ${orderDetails.orderId}</title>
                <style>
                    @page { size: portrait; margin: 10mm; }
                    table { page-break-inside: auto; }
                    tr { page-break-inside: avoid !important; break-inside: avoid !important; }
                    thead { display: table-header-group !important; page-break-after: avoid !important; }
                    .bottom-section-wrap { page-break-inside: avoid !important; break-inside: avoid !important; }
                    body {
                        font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                        margin: 0;
                        padding: 20px;
                        color: #000;
                        font-size: 11px;
                        background: #fff;
                    }
                    .label-copy {
                        font-size: 10px;
                        color: #555;
                        text-transform: uppercase;
                        font-weight: 600;
                        letter-spacing: 0.5px;
                        margin-bottom: 4px;
                    }
                    .invoice-box {
                        border: 1px solid #000;
                        background: #fff;
                    }
                    .header-flex {
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-start;
                        padding: 16px;
                        border-bottom: 1px solid #000;
                    }
                    .logo-wrap {
                        display: flex;
                        align-items: center;
                        gap: 16px;
                    }
                    .logo-wrap img {
                        height: 48px;
                        width: auto;
                        background: #2C3E2B;
                        padding: 6px;
                        border-radius: 4px;
                        display: block;
                    }
                    .company-name {
                        font-size: 22px;
                        font-weight: 800;
                        color: #2C3E2B;
                        margin-bottom: 4px;
                    }
                    .inv-meta {
                        text-align: right;
                        font-size: 11px;
                        line-height: 1.6;
                    }
                    .two-col {
                        display: flex;
                        border-bottom: 1px solid #000;
                    }
                    .col-half {
                        width: 50%;
                        padding: 12px;
                        box-sizing: border-box;
                    }
                    .col-half:first-child {
                        border-right: 1px solid #000;
                    }
                    .col-title {
                        font-weight: bold;
                        font-size: 12px;
                        text-transform: uppercase;
                        border-bottom: 2px solid #e5e7eb;
                        display: inline-block;
                        padding-bottom: 2px;
                        margin-bottom: 8px;
                    }
                    .col-content {
                        line-height: 1.6;
                    }
                    table.items-tbl {
                        width: 100%;
                        border-collapse: collapse;
                        text-align: left;
                    }
                    table.items-tbl th {
                        background: #2C3E2B;
                        color: #fff;
                        padding: 8px;
                        font-size: 11px;
                        font-weight: 600;
                        border-right: 1px solid #000;
                        border-bottom: 1px solid #000;
                    }
                    table.items-tbl th:last-child {
                        border-right: none;
                    }
                    .summary-flex {
                        display: flex;
                        justify-content: flex-end;
                        padding: 12px 16px;
                        border-top: 1px solid #000;
                    }
                    .summary-wrap {
                        width: 250px;
                        font-size: 13px;
                    }
                    .summary-row {
                        display: flex;
                        justify-content: space-between;
                        padding: 3px 0;
                    }
                    .summary-row.bold {
                        font-weight: bold;
                    }
                    .summary-row.border-top {
                        border-top: 1px solid #e5e7eb;
                        padding-top: 6px;
                        margin-top: 4px;
                    }
                    .bottom-flex {
                        display: flex;
                        border-top: 1px solid #000;
                    }
                    .bank-col {
                        width: 50%;
                        padding: 12px;
                        border-right: 1px solid #000;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        box-sizing: border-box;
                    }
                    .sign-col {
                        width: 50%;
                        padding: 12px;
                        display: flex;
                        flex-direction: column;
                        justify-content: flex-end;
                        align-items: center;
                        padding-bottom: 24px;
                        box-sizing: border-box;
                    }
                    .sign-line {
                        border-top: 1px solid #000;
                        width: 80%;
                        text-align: center;
                        padding-top: 8px;
                        font-weight: 600;
                        font-size: 13px;
                    }
                    .bank-table {
                        width: 100%;
                        font-size: 11px;
                        border-collapse: collapse;
                    }
                    .bank-table td {
                        padding: 2px 0;
                    }
                    .bank-table td.label {
                        font-weight: 600;
                    }
                    .bank-table td.val {
                        text-align: right;
                    }
                    .footer-msg {
                        text-align: center;
                        font-size: 10px;
                        color: #666;
                        margin-top: 16px;
                        line-height: 1.4;
                    }
                </style>
            </head>
            <body>
                <div class="label-copy">ORIGINAL FOR RECIPIENT</div>
                <div class="invoice-box">
                    <div class="header-flex">
                        <div class="logo-wrap">
                            <img src="${window.location.origin}/logo.png" alt="ChefCo Logo" />
                            <div>
                                <div class="company-name">CHEFCO HOSPITALITY PARTNERS LLP</div>
                            </div>
                        </div>
                        <div class="inv-meta">
                            <strong>Invoice No:</strong> ${orderDetails.orderId}<br>
                            <strong>Invoice Date:</strong> ${orderDetails.purchaseDate}
                        </div>
                    </div>

                    <div class="two-col">
                        <div class="col-half">
                            <div class="col-title">SHIPPED FROM</div>
                            <div class="col-content">
                                <strong>CHEFCO HOSPITALITY PARTNERS LLP</strong><br>
                                11/340, KT SONS Building. NOOLPUZHA,<br>
                                Sulthan Bathery, Wayanad,<br>
                                Kerala, 673592<br>
                                <strong>Company GST No:</strong> 32AAWFC5009M1Z5<br>
                                support@chefco.in
                            </div>
                        </div>
                        <div class="col-half">
                            <div class="col-title">BILL TO</div>
                            <div class="col-content">
                                <strong>${orderDetails.address?.fullName || '—'}</strong><br>
                                ${addressParts ? `${addressParts}<br>` : ''}
                                Phone: ${orderDetails.address?.mobileNumber || '—'}<br>
                                PAN Number: —<br>
                                GSTIN: ${orderDetails.address?.gstin || orderDetails.address?.gstNo || orderDetails.gstin || '—'}<br>
                                Place of Supply: Kerala
                            </div>
                        </div>
                    </div>

                    <table class="items-tbl">
                        <thead>
                            <tr>
                                <th style="width: 30px; text-align: center;">#</th>
                                <th>Item Name</th>
                                <th style="width: 50px; text-align: center;">HSN</th>
                                <th style="width: 50px; text-align: center;">Quantity</th>
                                <th style="width: 50px; text-align: center;">Unit</th>
                                <th style="width: 75px; text-align: right;">Price/Unit</th>
                                <th style="width: 85px; text-align: right;">Taxable Amount</th>
                                <th style="width: 70px; text-align: center;">GST</th>
                                <th style="width: 80px; text-align: right;">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${itemsRowsHtml}
                            <tr style="font-weight: bold; background: #fff; border-top: 1px solid #000;">
                                <td colspan="6" style="padding: 8px; text-align: right; border-right: 1px solid #000;">Total</td>
                                <td style="padding: 8px; text-align: right; border-right: 1px solid #000;">₹${totalTaxableAmount.toFixed(2)}</td>
                                <td style="padding: 8px; text-align: center; border-right: 1px solid #000;">₹${totalGstAmount.toFixed(2)}</td>
                                <td style="padding: 8px; text-align: right;">₹${(totalTaxableAmount + totalGstAmount).toFixed(2)}</td>
                            </tr>
                        </tbody>
                    </table>

                    <div class="bottom-section-wrap" style="page-break-inside: avoid; break-inside: avoid;">
                        <div class="summary-flex">
                            <div class="summary-wrap">
                                ${disc > 0 ? `<div class="summary-row" style="color: #dc2626;"><span>Discount</span><span>-₹${disc.toFixed(2)}</span></div>` : ''}
                                ${ship > 0 ? `<div class="summary-row"><span>Delivery</span><span>₹${ship.toFixed(2)}</span></div>` : ''}
                                ${proc > 0 ? `<div class="summary-row"><span>Processing Fee</span><span>₹${proc.toFixed(2)}</span></div>` : ''}
                                <div class="summary-row"><span>CGST</span><span>₹${cgstVal}</span></div>
                                <div class="summary-row"><span>SGST</span><span>₹${sgstVal}</span></div>
                                <div class="summary-row bold border-top"><span>Total GST</span><span>₹${t.toFixed(2)}</span></div>
                                <div class="summary-row bold" style="font-size: 15px; margin-top: 6px;"><span>Grand Total</span><span>₹${finalAmt.toFixed(2)}</span></div>
                            </div>
                        </div>

                        <div class="bottom-flex">
                            <div class="bank-col">
                                <div style="flex: 1; padding-right: 12px;">
                                    <div style="font-weight: bold; font-size: 12px; margin-bottom: 8px;">Bank Details</div>
                                    <table class="bank-table">
                                        <tr><td class="label">Account holder:</td><td class="val">chefco hospitality partners llp</td></tr>
                                        <tr><td class="label">Account number:</td><td class="val">10690200020234</td></tr>
                                        <tr><td class="label">Bank:</td><td class="val">Federal Bank</td></tr>
                                        <tr><td class="label">Branch:</td><td class="val">sulthan bathery</td></tr>
                                        <tr><td class="label">IFSC code:</td><td class="val">FDRL0001069</td></tr>
                                        <tr><td class="label">UPI ID:</td><td class="val">chefcobathery1qr@fbl</td></tr>
                                    </table>
                                </div>
                                <div>
                                    <img src="${qrCodeImage}" alt="UPI QR" style="width: 80px; height: 80px; object-fit: contain;" />
                                </div>
                            </div>
                            <div class="sign-col">
                                <div class="sign-line">Authorized Signatory</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="footer-msg">
                    <strong>Thank You For Your Business !</strong><br>
                    This is a computer generated bill.
                </div>
            </body>
            </html>
        `;
    };

    const handleDownloadInvoice = (orderDetails) => {
        if (!orderDetails) return;
        const htmlContent = getInvoiceHTML(orderDetails);
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ChefCo_Invoice_${orderDetails.orderId}.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handlePrintInvoice = (orderDetails) => {
        if (!orderDetails) return;
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            toast.error("Popup blocker prevented opening the print window. Please allow popups for this site.");
            return;
        }
        const htmlContent = getInvoiceHTML(orderDetails);
        printWindow.document.write(htmlContent + `
            <script>
                window.onload = function() {
                    window.print();
                    window.onafterprint = function() {
                        window.close();
                    };
                };
            </script>
        `);
        printWindow.document.close();
    };

    const handleAddressSelect = (index) => {
        const user = auth.currentUser;
        if (user) {
            const selKey = `selectedAddressIndex_${user.email.toLowerCase()}`;
            localStorage.setItem(selKey, index.toString());
        }
        if (addresses[index]) {
            setSelectedAddress(addresses[index]);
        }
        setIsSavedAddressModalOpen(false);
    };

    const handleAddNewAddress = () => {
        setIsSavedAddressModalOpen(false);
        setAddressModalMode('create');
        setEditAddressIndex(null);
        setIsAddressModalOpen(true);
    };

    const handleEditAddress = (index) => {
        setIsSavedAddressModalOpen(false);
        setAddressModalMode('edit');
        setEditAddressIndex(index);
        setIsAddressModalOpen(true);
    };

    const handleAddressSave = async () => {
        const user = auth.currentUser;
        if (user) {
            try {
                let userDocRef = doc(db, "users", user.uid);
                let docSnap = await getDoc(userDocRef);
                if (!docSnap.exists()) {
                    userDocRef = doc(db, "users", user.email.toLowerCase());
                    docSnap = await getDoc(userDocRef);
                }
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    const addrList = data.addresses || [];
                    setAddresses(addrList);

                    const selKey = `selectedAddressIndex_${user.email.toLowerCase()}`;
                    if (addressModalMode === 'create') {
                        const newIndex = addrList.length - 1;
                        localStorage.setItem(selKey, newIndex.toString());
                        setSelectedAddress(addrList[newIndex]);
                    } else {
                        const selectedIndex = parseInt(localStorage.getItem(selKey) || '0', 10);
                        if (selectedIndex === editAddressIndex && addrList[editAddressIndex]) {
                            setSelectedAddress(addrList[editAddressIndex]);
                        }
                    }
                }
            } catch (e) {
                console.error("Error reloading addresses after save", e);
            }
        }
        setIsAddressModalOpen(false);
        setIsSavedAddressModalOpen(true);
    };

    const handleBackToSaved = () => {
        setIsAddressModalOpen(false);
        setIsSavedAddressModalOpen(true);
    };

    if (loggedIn && isCartLoading) {
        return <LoadingScreen />;
    }

    if (loggedIn && !isCartLoading && cart.length === 0) {
        return (
            <div className="bg-[#F2F2F2] min-h-screen py-16 flex items-center justify-center px-4">
                <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center border border-gray-100">
                    <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6">
                        <ShoppingBag className="w-10 h-10 text-[#2C3E2B]" />
                    </div>
                    <h2 className="text-2xl font-black text-gray-900 mb-2">Your Cart is Empty</h2>
                    <p className="text-sm text-gray-500 mb-6">
                        You don't have any items in your cart. Add some products before checking out!
                    </p>
                    <Link
                        to="/products"
                        className="inline-flex items-center justify-center gap-2 w-full py-3.5 bg-[#2C3E2B] hover:bg-[#1e2d1e] text-white rounded-2xl font-black text-sm uppercase tracking-widest shadow-md transition-all hover:-translate-y-0.5 border-none cursor-pointer text-center no-underline font-medium"
                    >
                        Browse Products
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-[#F2F2F2] min-h-screen py-8">
            <div className="max-w-7x2 mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="text-3xl font-extrabold text-gray-900 mb-8 md:ms-40">Checkout</h2>

                {/* ── LOGIN GATE ────────────────────────────────────────── */}
                {!loggedIn ? (
                    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
                        <div className="bg-white rounded-3xl shadow-xl px-10 py-14 max-w-md w-full border border-gray-100">
                            <div className="w-20 h-20 rounded-full bg-[#2C3E2B]/8 flex items-center justify-center mx-auto mb-6">
                                <Lock className="w-9 h-9 text-[#2C3E2B]" />
                            </div>
                            <h3 className="text-2xl font-black text-gray-900 mb-2">Sign In to Checkout</h3>
                            <p className="text-sm text-gray-500 mb-2">
                                You must be logged in to complete your purchase.
                            </p>
                            {cart.length > 0 && (
                                <p className="text-xs font-semibold text-[#2C3E2B] bg-[#2C3E2B]/8 rounded-xl px-4 py-2 mb-6 inline-block">
                                    🛒 {cart.length} item{cart.length !== 1 ? 's' : ''} saved in your cart — they'll be here when you log in.
                                </p>
                            )}
                            <button
                                onClick={onAuthClick}
                                className="w-full py-3.5 bg-[#2C3E2B] hover:bg-[#1e2d1e] text-white rounded-2xl font-black text-sm uppercase tracking-widest shadow-md transition-all hover:-translate-y-0.5 border-none cursor-pointer mb-3"
                            >
                                Sign In / Register
                            </button>
                            <Link
                                to="/products"
                                className="block text-xs font-semibold text-slate-400 hover:text-slate-600 transition mt-1"
                            >
                                ← Continue Shopping
                            </Link>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">

                        {/* Left Column */}
                        <div className="lg:col-span-5 lg:col-start-2 space-y-6">

                            {/* Items List Card */}
                            <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 overflow-hidden px-5">
                                <div className="p-5 border-b border-slate-100 flex items-center gap-3 bg-slate-50/50">
                                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-base font-bold text-gray-900">Items in your Order</h3>
                                        <p className="text-xs text-slate-500 font-medium">{cart.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)} items total</p>
                                    </div>
                                </div>
                                <div className="p-2">
                                    <div className="flex flex-col">
                                        {/* Items Section */}
                                        <div className="space-y-4 max-h-[340px] overflow-y-auto pr-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                                            {cart.length > 0 ? (
                                                cart.map((item, idx) => (
                                                    <div key={idx} className="flex items-center justify-between">
                                                        <div className="flex items-center space-x-4">
                                                            <div className="w-16 h-16 bg-gray-100 rounded-md overflow-hidden flex-shrink-0">
                                                                <img src={item.product.image} alt={item.product.title} className="w-full h-full object-cover" />
                                                            </div>
                                                            <div>
                                                                <h4 className="text-[18px] font-bold text-gray-900 line-clamp-1">{item.product.title}</h4>
                                                                <p className="text-xs text-slate-500 font-semibold mt-1.5 mb-2 flex items-center gap-1.5">
                                                                    <span>SKU: {item.product.code || 'N/A'}</span>
                                                                    {item.product.brand && (
                                                                        <>
                                                                            <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                                                                            <span>{item.product.brand}</span>
                                                                        </>
                                                                    )}
                                                                </p>
                                                                <p className="text-sm text-gray-500 mt-1 font-semibold">
                                                                    Price: ₹{getProductPriceForQty(item.product, Number(item.quantity) || 0).toFixed(2)} each
                                                                </p>
                                                                {item.product.taxApplicable && (
                                                                    <span className="inline-flex items-center text-[9px] font-black uppercase tracking-wide bg-amber-50 text-amber-600 border border-amber-200 px-1.5 py-0.5 rounded-md mt-1">
                                                                        +Tax Applicable
                                                                    </span>
                                                                )}
                                                                {item.product.stock !== undefined && item.product.stock < (item.product.minimum_order || 1) ? (
                                                                    <span className="inline-flex items-center text-[9px] font-black uppercase tracking-wide bg-red-50 text-red-600 border border-red-200 px-1.5 py-0.5 rounded-md mt-1 ml-1.5 font-medium">
                                                                        Out of Stock
                                                                    </span>
                                                                ) : item.product.stock !== undefined && item.quantity > item.product.stock ? (
                                                                    <span className="inline-flex items-center text-[9px] font-black uppercase tracking-wide bg-amber-50 text-amber-600 border border-amber-200 px-1.5 py-0.5 rounded-md mt-1 ml-1.5 font-medium">
                                                                        Insufficient Stock (Available: {item.product.stock})
                                                                    </span>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-4">

                                                            <p className="text-sm font-semibold text-gray-900">Qty: {item.quantity}</p>
                                                            <span className="text-md font-bold text-gray-900">
                                                                ₹{(getProductPriceForQty(item.product, Number(item.quantity) || 0) * item.quantity).toFixed(2)}
                                                            </span>
                                                            <button
                                                                onClick={() => handleRemoveFromCart(idx)}
                                                                className="text-red-500 hover:text-red-700 transition hover:-translate-y-0.5 "
                                                                title="Remove item"
                                                            >
                                                                <X size={18} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <p className="text-sm text-gray-500">Your cart is empty.</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                             {/* Modern Delivery Address Card */}
                            <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 overflow-hidden">
                                <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-[#2C3E2B]/10 flex items-center justify-center text-[#2C3E2B]">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.243-4.243a8 8 0 1111.314 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h3 className="text-base font-bold text-gray-900">Delivery Address</h3>
                                            <p className="text-xs text-slate-500 font-medium">Where should we send your order?</p>
                                        </div>
                                    </div>
                                    <button onClick={() => setIsSavedAddressModalOpen(true)} className="text-sm font-bold text-[#2C3E2B] bg-[#2C3E2B]/10 hover:bg-[#2C3E2B]/20 px-4 py-2 rounded-lg transition-colors border-none cursor-pointer">
                                        {selectedAddress ? 'Change' : 'Add Address'}
                                    </button>
                                </div>
                                <div className="p-5">
                                    {selectedAddress ? (
                                        <div className="flex gap-4">
                                            <div className="flex-1 space-y-1.5">
                                                <p className="text-base font-extrabold text-gray-900">{selectedAddress.fullName}</p>
                                                <p className="text-sm text-slate-600 leading-relaxed">
                                                    {selectedAddress.streetAddress}
                                                    {selectedAddress.unitNumber && `, Unit ${selectedAddress.unitNumber}`}<br />
                                                    {selectedAddress.city}, {selectedAddress.state} {selectedAddress.zipCode}<br />
                                                    {selectedAddress.country}
                                                </p>
                                                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100">
                                                    <div className="flex items-center gap-1.5 text-sm text-slate-600">
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
                                                            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                                        </svg>
                                                        {selectedAddress.mobileNumber}
                                                    </div>
                                                    <div className="flex items-center gap-1.5 text-sm text-slate-600">
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
                                                            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                                            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                                        </svg>
                                                        {selectedAddress.email}
                                                    </div>
                                                </div>
                                                {selectedAddress.gstin && (
                                                    <div className="mt-2 text-xs font-bold text-slate-700 bg-slate-100 px-2.5 py-1 rounded w-fit">
                                                        GSTIN: {selectedAddress.gstin}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center py-6 border border-dashed border-slate-300 rounded-xl bg-slate-50">
                                            <p className="text-sm font-medium text-slate-500 mb-3">No address selected for delivery.</p>
                                            <button onClick={() => setIsSavedAddressModalOpen(true)} className="btn-primary px-6 py-2 shadow-md">Add an Address</button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Right Column: Checkout */}
                        <div className="lg:col-span-5">
                            
                            <div className="bg-white rounded-xl shadow-md overflow-hidden sticky top-24">
                                <div className="p-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>

                                    {/* Order Summary Totals */}
                                    <div className="mb-2 bg-slate-50 border border-slate-100 rounded-xl p-4 space-y-3 text-left">
                                        <div className="flex justify-between text-xs text-slate-500 pb-3 border-b border-slate-200/60">
                                            <span className="font-semibold">Number of Items</span>
                                            <span className="font-extrabold">{cart.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)} items</span>
                                        </div>
                                        <div className="flex justify-between text-sm pt-1 pb-1">
                                            <span className="text-gray-500 font-semibold">Subtotal</span>
                                            <span className="font-extrabold text-[#2C3E2B]">₹{subtotal.toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between text-sm pb-1">
                                            <span className="text-gray-500 font-semibold">Discount</span>
                                            <span className="font-extrabold text-green-600">-₹{discount.toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between text-sm pb-1">
                                            <span className="text-gray-500 font-semibold">Tax</span>
                                            <span className="font-extrabold text-[#2C3E2B]">₹{tax.toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between text-sm pb-1">
                                            <span className="text-gray-500 font-semibold">Shipping Charge</span>
                                            <span className="font-extrabold text-[#2C3E2B]">₹{shipping.toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between text-sm pb-1">
                                            <span className="text-gray-500 font-semibold">Processing Fee</span>
                                            <span className="font-extrabold text-[#2C3E2B]">₹{processing.toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between text-sm pb-1">
                                            <span className="text-gray-500 font-semibold">Payment Option</span>
                                            <span className="font-extrabold text-[#2C3E2B]">COD</span>
                                        </div>
                                        <div className="border-t border-slate-200 pt-3 flex justify-between items-baseline">
                                            <span className="text-sm font-black text-gray-900">Total</span>
                                            <span className="text-xl font-black text-[#2C3E2B]">₹{total.toFixed(2)}</span>
                                        </div>
                                    </div>
                                    </div>
                                    <div className="h-px bg-gray-100 my-2"></div>

                                    {/* Order Notes */}
                                    <div className="mb-6 ms-6">
                                        <label className="block text-sm font-bold text-gray-800 mb-2">
                                            Order Notes
                                        </label>
                                        <textarea
                                            rows="3"
                                            value={orderNotes}
                                            onChange={(e) => setOrderNotes(e.target.value)}
                                            placeholder="Add any special instructions..."
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2C3E2B]/20 focus:border-[#2C3E2B] transition-all resize-none"
                                        ></textarea>
                                    </div>

                                    {hasInsufficientStockItems && (
                                        <p className="text-xs font-bold text-red-600 mb-3 text-center">
                                            Some items in your cart have insufficient stock. Please resolve them in your cart to proceed.
                                        </p>
                                    )}

                                    {/* Checkout Button */}
                                    <button
                                        onClick={handleCheckout}
                                        disabled={isPlacingOrder || hasInsufficientStockItems}
                                        className={`btn-primary w-full py-3 shadow-md hover:shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed ${hasInsufficientStockItems ? 'opacity-50 cursor-not-allowed bg-slate-400 border-none' : ''}`}
                                    >
                                        {isPlacingOrder ? 'Placing Order...' : 'Proceed to Checkout'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    
                )}
                
            </div>

            {/* Popup Modal */}

            {showPopup && popupStatus === 'error' && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden relative">
                        <button
                            onClick={() => setShowPopup(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X size={20} />
                        </button>

                        <div className="p-6 text-center">
                            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                                <XCircle className="h-6 w-6 text-red-600" />
                            </div>

                            <h3 className="text-lg font-bold text-gray-900 mb-2">
                                Oops!
                            </h3>
                            <p className="text-sm text-gray-600 mb-6">
                                {popupMessage}
                            </p>

                            <button
                                onClick={() => setShowPopup(false)}
                                className="w-full py-2.5 px-4 rounded-lg font-medium text-white transition-colors bg-red-600 hover:bg-red-700"
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Address Modals */}
            <SavedAddress
                isOpen={isSavedAddressModalOpen}
                onClose={() => setIsSavedAddressModalOpen(false)}
                onAddressSelect={handleAddressSelect}
                onAddNew={handleAddNewAddress}
                onEdit={handleEditAddress}
            />
            <Address
                isOpen={isAddressModalOpen}
                onClose={() => setIsAddressModalOpen(false)}
                mode={addressModalMode}
                addressIndex={editAddressIndex}
                onSave={handleAddressSave}
                onBackToSaved={handleBackToSaved}
            />
        </div>
    );
}

